import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PAYMENT_METHOD_LABELS,
  MP_METHODS,
  PAYMENT_METHODS,
  posSettingsSchema,
  SCALE_PRESETS,
  SHELF_LIFE_GROUP_LABELS,
  SHELF_LIFE_GROUPS,
  scaleValueLength,
  type PaymentMethod,
  type PosSettings,
  type ScaleConfig,
  type ScaleTestResult,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { Archive, Barcode, CircleHelp, Plug, Printer, Save, ScanLine, Sparkles, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { printSale, sampleSale, ticketHtml } from '../../lib/ticket-print';
import { formatQty, pesosToCents } from '../../lib/units';
import { DevicesTab } from './DevicesTab';
import { FiscalTab } from './FiscalTab';
import { FranchiseStatusCard } from './FranchiseStatusCard';
import { MercadoPagoCard } from './MercadoPagoCard';

type Tab = 'guide' | 'devices' | 'scale' | 'ticket' | 'sales' | 'fiscal' | 'expiry' | 'team';
const TABS: { key: Tab; label: string }[] = [
  { key: 'guide', label: 'Guía' },
  { key: 'devices', label: 'Esta caja' },
  { key: 'scale', label: 'Balanza' },
  { key: 'ticket', label: 'Ticket' },
  { key: 'sales', label: 'Pagos' },
  { key: 'fiscal', label: 'Facturación' },
  { key: 'expiry', label: 'Vencimientos' },
  { key: 'team', label: 'Equipo' },
];

/**
 * Configuración del mostrador. Pensada para que cada carnicería la haga sola:
 * cada opción explica qué hace y hay pruebas en vivo (leer una etiqueta, imprimir un ticket de muestra).
 */
export function PosSettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const saved = useQuery({
    queryKey: ['pos', 'settings'],
    queryFn: () => api.get<PosSettings>('/pos/settings'),
    enabled: can('settings.manage'),
  });
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(location.search).get('tab') as Tab) || 'guide');
  const [draft, setDraft] = useState<PosSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (saved.data && !draft) setDraft(saved.data);
  }, [saved.data, draft]);

  const dirty = useMemo(() => !!draft && !!saved.data && JSON.stringify(draft) !== JSON.stringify(saved.data), [draft, saved.data]);

  async function save() {
    if (!draft) return;
    const parsed = posSettingsSchema.safeParse(draft);
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      const res = await api.put<PosSettings>('/pos/settings', parsed.data);
      qc.setQueryData(['pos', 'settings'], res);
      setDraft(res);
      await Promise.all(['pos', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success('Configuración guardada', 'Las cajas toman los cambios al instante.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Encargado de una franquicia con permiso solo para sus cuentas: ve Mercado Pago y facturación, nada más.
  if (!can('settings.manage')) {
    return (
      <>
        <PageHeader
          title="Cuentas de la franquicia"
          subtitle="Tu franquicia cobra con su propio Mercado Pago y factura con su CUIT. La plata va directo a su cuenta."
        />
        <MercadoPagoCard />
        <FiscalTab />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Configuración"
        subtitle="Balanza, cajón, impresora de tickets, lector, formas de pago y vencimientos. Todo se prueba desde acá antes de usarlo."
        actions={
          tab !== 'guide' &&
          tab !== 'devices' &&
          tab !== 'fiscal' && (
            <Button variant="primary" icon={<Save />} loading={busy} disabled={!dirty} onClick={() => void save()}>
              Guardar cambios
            </Button>
          )
        }
      />
      <div className="bf-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
      {dirty && tab !== 'guide' && tab !== 'devices' && tab !== 'fiscal' && <Callout tone="warning">Tenés cambios sin guardar.</Callout>}
      {!draft ? (
        <Skeleton style={{ height: 240 }} />
      ) : tab === 'guide' ? (
        <Guide goTo={setTab} />
      ) : tab === 'devices' ? (
        <DevicesTab ticket={draft.ticket} />
      ) : tab === 'fiscal' ? (
        <FiscalTab />
      ) : tab === 'scale' ? (
        <ScaleTab value={draft.scale} onChange={(scale) => setDraft({ ...draft, scale })} />
      ) : tab === 'ticket' ? (
        <TicketTab value={draft} onChange={(ticket) => setDraft({ ...draft, ticket })} />
      ) : tab === 'expiry' ? (
        <ExpiryTab value={draft.stock} onChange={(stock) => setDraft({ ...draft, stock })} />
      ) : tab === 'team' ? (
        <TeamTab value={draft.team} onChange={(team) => setDraft({ ...draft, team })} />
      ) : (
        <SalesTab value={draft.sales} onChange={(sales) => setDraft({ ...draft, sales })} />
      )}
    </>
  );
}

/* ---------------------------------------------------------------- Guía */
function GuideStep({ n, title, children, action }: { n: number; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="bf-guide-step">
      <span className="bf-guide-n">{n}</span>
      <div>
        <h3>{title}</h3>
        <div className="bf-guide-body">{children}</div>
        {action && <div style={{ marginTop: 10 }}>{action}</div>}
      </div>
    </div>
  );
}

function Guide({ goTo }: { goTo: (t: Tab) => void }) {
  return (
    <Card>
      <CardHeader
        title="Cómo armar el mostrador"
        subtitle="Funciona con una computadora o notebook con Chrome o Edge. No hace falta instalar nada de BifeAI."
      />
      <CardBody>
        <div className="bf-guide">
          <GuideStep n={1} title="Lector de código de barras">
            <p>
              Cualquier lector USB o inalámbrico sirve. Viene configurado como <strong>teclado</strong>: al leer, “escribe” el código y aprieta Enter.
              Enchufalo y probalo abriendo el Bloc de notas: si aparecen los números, ya está.
            </p>
            <p>
              Si no manda Enter al final, buscá en el manual del lector la hoja de configuración y escaneá el código <em>“Add CR suffix”</em> o{' '}
              <em>“Enter”</em>.
            </p>
          </GuideStep>
          <GuideStep
            n={2}
            title="Balanza con etiquetas (opción A)"
            action={
              <Button icon={<Barcode />} onClick={() => goTo('scale')}>
                Configurar balanza
              </Button>
            }
          >
            <p>
              Kretz, Systel, Moretti, Dibal y la mayoría de las importadas imprimen una etiqueta con código de barras que lleva el{' '}
              <strong>código del producto (PLU)</strong> y el
              <strong> peso</strong> o el <strong>importe</strong>. BifeAI lo lee y carga el producto solo.
            </p>
            <ul>
              <li>En la balanza, cada producto tiene que tener el mismo PLU que cargaste en BifeAI (Productos → código de balanza).</li>
              <li>
                Si no sabés qué formato usa, imprimí 2 o 3 etiquetas y usá <strong>Detectar automáticamente</strong>.
              </li>
              <li>
                Recomendado: que la balanza imprima el <strong>peso</strong> (no el importe). Así el precio siempre es el de BifeAI.
              </li>
            </ul>
          </GuideStep>
          <GuideStep
            n={3}
            title="Balanza conectada a la computadora (opción B)"
            action={
              <Button icon={<Plug />} onClick={() => goTo('devices')}>
                Conectar balanza
              </Button>
            }
          >
            <p>
              Si la balanza está al lado de la caja y no imprime etiquetas, se conecta con un cable a la computadora: el cajero toca el producto y{' '}
              <strong>el peso aparece solo</strong>. Va por USB o con un adaptador RS-232 a USB. No hay que instalar nada: se conecta desde Chrome o
              Edge.
            </p>
            <p>
              Podés usar las dos opciones a la vez (por ejemplo, etiquetas para lo que se pesa atrás y balanza en la caja para lo que se pesa
              adelante).
            </p>
          </GuideStep>
          <GuideStep
            n={4}
            title="Impresora de tickets"
            action={
              <Button icon={<Printer />} onClick={() => goTo('ticket')}>
                Configurar ticket
              </Button>
            }
          >
            <p>
              Sirve cualquier impresora térmica de 58 u 80 mm (Epson, Hasar, 3nStar, Gadnic, Xprinter y similares) instalada en Windows con su driver.
            </p>
            <ul>
              <li>
                Ponela como <strong>impresora predeterminada</strong> en Windows.
              </li>
              <li>
                La primera vez que imprimas, en el cuadro de Chrome elegí esa impresora, <strong>Márgenes: ninguno</strong> y desactivá “Encabezados y
                pies de página”. Chrome lo recuerda.
              </li>
              <li>
                Para que imprima <strong>sin preguntar</strong>: creá un acceso directo de Chrome y al final del destino agregá{' '}
                <code>--kiosk-printing</code>. Abrí BifeAI desde ese acceso.
              </li>
            </ul>
          </GuideStep>
          <GuideStep
            n={5}
            title="Cajón de dinero"
            action={
              <Button icon={<Archive />} onClick={() => goTo('devices')}>
                Configurar cajón
              </Button>
            }
          >
            <p>
              Se enchufa a la <strong>impresora de tickets</strong> (no a la computadora) y se abre solo al imprimir el ticket de cada venta. Para
              abrirlo sin vender, el cajero toca <strong>Abrir cajón</strong> y escribe el motivo: queda registrado.
            </p>
          </GuideStep>
          <GuideStep n={6} title="Una caja por computadora">
            <p>
              Cada computadora del mostrador puede registrarse como <strong>terminal</strong> desde Sucursales. Así cada una lleva su numeración de
              tickets y su propia caja, y los cajeros pueden cambiar de turno con su <strong>PIN</strong> sin cerrar sesión.
            </p>
          </GuideStep>
          <GuideStep
            n={7}
            title="Mercado Pago, posnet, Cuenta DNI y otros cobros"
            action={
              <Button icon={<Wand2 />} onClick={() => goTo('sales')}>
                Ver medios de pago
              </Button>
            }
          >
            <p>
              El <strong>posnet del banco</strong> no se conecta a la computadora: se cobra en el aparato el mismo importe y en BifeAI se elige{' '}
              <strong>Débito</strong> o <strong>Crédito</strong> (con las cuotas) cuando dice “Aprobado”. Con <strong>Cuenta DNI</strong>,
              transferencias u otras billeteras es igual: se confirma cuando el pago aparece aprobado. Así cada cobro queda separado en la caja y en
              el Dashboard. Si tenés más de un posnet, cargalos en <strong>Pagos</strong> para controlar cada uno con su cierre de lote.
            </p>
            <p>
              Con <strong>Mercado Pago</strong> conectado (también en Pagos) es automático: el QR sale con el importe exacto, el posnet Point recibe
              el importe solo y la venta se confirma sola cuando se aprueba el pago. Las cajas de todas las sucursales propias las crea la casa
              central desde acá.
            </p>
            <p>
              ¿Tenés <strong>franquicias</strong>? Marcalas en <strong>Sucursales</strong> (“Es una franquicia”): cada una conecta su propio Mercado
              Pago y factura con su CUIT. La plata va al franquiciado y vos ves todo lo que vende y factura.
            </p>
          </GuideStep>
          <GuideStep n={8} title="Primera venta de prueba">
            <p>Abrí la caja, escaneá una etiqueta, cobrá y anulá la venta desde Ventas. Si todo sale bien, el mostrador está listo.</p>
          </GuideStep>
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- Balanza */
function Help({ children }: { children: ReactNode }) {
  return (
    <p className="bf-help">
      <CircleHelp aria-hidden />
      <span>{children}</span>
    </p>
  );
}

function SwitchRow({ title, help, checked, onChange }: { title: string; help: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="bf-switch-row">
      <div>
        <strong>{title}</strong>
        <small>{help}</small>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} label={title} />
    </div>
  );
}

/** Dibuja el código separado en partes con colores, para entender el formato. */
function CodeAnatomy({ c }: { c: ScaleConfig }) {
  const pre = c.prefixes[0]?.length ?? 2;
  const parts: { label: string; len: number; kind: string }[] = [
    { label: 'Prefijo', len: pre, kind: 'pre' },
    { label: 'PLU', len: c.pluLength, kind: 'plu' },
    ...(c.innerCheckDigit ? [{ label: 'Ctrl', len: 1, kind: 'chk' }] : []),
    {
      label: c.valueType === 'WEIGHT' ? 'Peso' : 'Importe',
      len: Math.max(scaleValueLength(c), 0),
      kind: 'val',
    },
    { label: 'Verif.', len: 1, kind: 'chk' },
  ];
  return (
    <div className="bf-anatomy" aria-label="Partes del código">
      {parts.map((p, i) => (
        <div key={i} className={`bf-anatomy-part bf-anatomy-part--${p.kind}`} style={{ flexGrow: p.len }}>
          <span className="bf-anatomy-digits">{'0'.repeat(p.len).replace(/0/g, '•')}</span>
          <span className="bf-anatomy-label">{p.label}</span>
        </div>
      ))}
    </div>
  );
}

function ScaleTab({ value: c, onChange }: { value: ScaleConfig; onChange: (c: ScaleConfig) => void }) {
  const [advanced, setAdvanced] = useState(c.preset === 'CUSTOM');
  const set = <K extends keyof ScaleConfig>(k: K, v: ScaleConfig[K]) => onChange({ ...c, [k]: v, preset: k === 'enabled' ? c.preset : 'CUSTOM' });
  const preset = SCALE_PRESETS.find((p) => p.key === c.preset);

  return (
    <div className="bf-settings-grid">
      <Card>
        <CardHeader title="Formato de la etiqueta" subtitle="Cómo arma el código de barras tu balanza." />
        <CardBody>
          <div className="bf-form">
            <SwitchRow
              title="Leer etiquetas de balanza"
              help="Apagalo si no usás balanza con etiquetas: los productos se venden tocándolos o escribiendo el peso."
              checked={c.enabled}
              onChange={(v) => onChange({ ...c, enabled: v })}
            />
            {c.enabled && (
              <>
                <Field label="Formato">
                  <Select
                    value={c.preset}
                    onChange={(e) => {
                      const p = SCALE_PRESETS.find((x) => x.key === e.target.value);
                      if (p) onChange({ ...c, ...p.config, preset: p.key });
                      else setAdvanced(true);
                    }}
                  >
                    {SCALE_PRESETS.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                    <option value="CUSTOM">Personalizado (lo armo yo)</option>
                  </Select>
                </Field>
                {preset && <Help>{preset.help}</Help>}
                <CodeAnatomy c={c} />
                <Help>
                  En el menú de la balanza este dato suele llamarse <em>“formato de código de barras”</em>, <em>“EAN”</em> o{' '}
                  <em>“tipo de etiqueta”</em>. Si no lo encontrás, usá la detección automática de al lado.
                </Help>
                <Button variant="ghost" onClick={() => setAdvanced((a) => !a)}>
                  {advanced ? 'Ocultar opciones avanzadas' : 'Opciones avanzadas'}
                </Button>
                {advanced && (
                  <div className="bf-form-grid">
                    <Field label="Largo del código" hint="Casi siempre 13 (EAN-13).">
                      <Input inputMode="numeric" value={c.length} onChange={(e) => set('length', Number(e.target.value.replace(/\D/g, '')) || 0)} />
                    </Field>
                    <Field label="Prefijos" hint="Separados por coma. Ej.: 20,21,22 o solo 2.">
                      <Input
                        value={c.prefixes.join(',')}
                        onChange={(e) =>
                          set(
                            'prefixes',
                            e.target.value
                              .split(/[,\s]+/)
                              .map((s) => s.replace(/\D/g, ''))
                              .filter(Boolean),
                          )
                        }
                      />
                    </Field>
                    <Field label="Dígitos del PLU">
                      <Select value={c.pluLength} onChange={(e) => set('pluLength', Number(e.target.value))}>
                        {[3, 4, 5, 6, 7].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="El código trae">
                      <Select value={c.valueType} onChange={(e) => set('valueType', e.target.value as ScaleConfig['valueType'])}>
                        <option value="WEIGHT">Peso</option>
                        <option value="PRICE">Importe</option>
                      </Select>
                    </Field>
                    {c.valueType === 'WEIGHT' ? (
                      <Field label="Unidad del peso" hint="Gramos es lo más común.">
                        <Select
                          value={c.weightUnitGrams}
                          onChange={(e) => set('weightUnitGrams', Number(e.target.value) as ScaleConfig['weightUnitGrams'])}
                        >
                          <option value={1}>Gramos (01250 = 1,250 kg)</option>
                          <option value={10}>Decagramos (00125 = 1,250 kg)</option>
                          <option value={100}>Cien gramos (00012 = 1,200 kg)</option>
                        </Select>
                      </Field>
                    ) : (
                      <Field label="El importe viene" hint="Si la etiqueta dice $ 22.875 y el código 022875, son pesos enteros.">
                        <Select
                          value={c.priceDecimals}
                          onChange={(e) => set('priceDecimals', Number(e.target.value) as ScaleConfig['priceDecimals'])}
                        >
                          <option value={0}>En pesos enteros</option>
                          <option value={1}>Con 1 decimal</option>
                          <option value={2}>Con centavos</option>
                        </Select>
                      </Field>
                    )}
                    <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 12 }}>
                      <SwitchRow
                        title="Dígito de control después del PLU"
                        help="Algunas balanzas agregan un dígito extra entre el producto y el peso."
                        checked={c.innerCheckDigit}
                        onChange={(v) => set('innerCheckDigit', v)}
                      />
                      <SwitchRow
                        title="Verificar el último dígito"
                        help="Detecta lecturas con error. Apagalo solo si tu balanza lo calcula mal y todas las etiquetas dan error."
                        checked={c.validateCheckDigit}
                        onChange={(v) => set('validateCheckDigit', v)}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {c.enabled && (
        <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
          <ScaleTester config={c} />
          <ScaleDetector onPick={(cfg) => onChange(cfg)} />
        </div>
      )}
    </div>
  );
}

function ScaleTester({ config }: { config: ScaleConfig }) {
  const [code, setCode] = useState('');
  const [res, setRes] = useState<ScaleTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function test(raw = code) {
    const barcode = raw.trim();
    if (!barcode) return;
    setBusy(true);
    setError(null);
    try {
      setRes(
        await api.post<ScaleTestResult>('/pos/scale/test', {
          barcode,
          scale: config,
        }),
      );
    } catch (err) {
      setRes(null);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const r = res?.reading;
  return (
    <Card>
      <CardHeader
        title="Probar una etiqueta"
        subtitle="Escaneá una etiqueta real con el lector (o escribí el número). Se prueba con el formato elegido, aunque no lo hayas guardado."
      />
      <CardBody>
        <form
          className="bf-form"
          onSubmit={(e) => {
            e.preventDefault();
            void test();
          }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Escaneá acá…"
              inputMode="numeric"
              aria-label="Código a probar"
            />
            <Button type="submit" icon={<ScanLine />} loading={busy}>
              Probar
            </Button>
          </div>
          {error && <Callout tone="danger">{error}</Callout>}
          {r && !r.ok && (
            <Callout tone="danger">
              {r.reason === 'NOT_SCALE'
                ? 'No es una etiqueta de balanza con este formato (revisá el largo y los prefijos). Si es un código de barras común, se busca por código del producto.'
                : r.reason === 'BAD_CHECK_DIGIT'
                  ? 'El último dígito no verifica: puede ser una mala lectura o que la balanza use otro formato.'
                  : 'El peso o importe del código es cero o inválido.'}
              {res?.product && (
                <>
                  {' '}
                  Encontrado como producto: <strong>{res.product.name}</strong>.
                </>
              )}
            </Callout>
          )}
          {r && r.ok && (
            <div className="bf-test-result">
              <dl className="bf-kv">
                <div>
                  <dt>PLU leído</dt>
                  <dd className="bf-num">{r.plu}</dd>
                </div>
                {r.grams !== null && (
                  <div>
                    <dt>Peso</dt>
                    <dd className="bf-num">{formatQty('WEIGHT', r.grams)}</dd>
                  </div>
                )}
                {r.priceCents !== null && (
                  <div>
                    <dt>Importe</dt>
                    <dd className="bf-num">{formatMoney(r.priceCents)}</dd>
                  </div>
                )}
                <div>
                  <dt>Producto</dt>
                  <dd>{res?.product ? res.product.name : <Badge tone="danger">No hay producto con PLU {r.plu}</Badge>}</dd>
                </div>
                {res?.line && (
                  <div>
                    <dt>En el ticket</dt>
                    <dd className="bf-num">
                      {formatQty(res.product?.unitType ?? 'WEIGHT', res.line.qty)} × {formatMoney(res.line.unitPriceCents)} ={' '}
                      <strong>{formatMoney(res.line.lineTotalCents)}</strong>
                    </dd>
                  </div>
                )}
              </dl>
              {res?.product && !res.product.priceCents && (
                <Callout tone="warning">El producto no tiene precio en esta sucursal. Cargalo en Precios.</Callout>
              )}
              {res?.product && res.line && <Callout tone="success">¡Lee bien! Así se va a cargar en el mostrador.</Callout>}
              {!res?.product && <Help>Cargá ese PLU en el producto (Productos → editar → código de balanza) para que lo reconozca.</Help>}
            </div>
          )}
        </form>
      </CardBody>
    </Card>
  );
}

interface Sample {
  code: string;
  plu: string;
  value: string;
}

function ScaleDetector({ onPick }: { onPick: (c: ScaleConfig) => void }) {
  const [kind, setKind] = useState<'WEIGHT' | 'PRICE'>('WEIGHT');
  const [samples, setSamples] = useState<Sample[]>([{ code: '', plu: '', value: '' }]);
  const [found, setFound] = useState<ScaleConfig[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const setSample = (i: number, k: keyof Sample, v: string) => setSamples((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  function toGrams(v: string): number | null {
    const n = Number(v.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return null;
    // "1,250" o "1.250" = kilos; "1250" (sin separador y > 50) = gramos.
    return /[.,]/.test(v) || n < 50 ? Math.round(n * 1000) : Math.round(n);
  }

  async function detect() {
    const body = samples
      .filter((s) => s.code.trim())
      .map((s) => ({
        code: s.code.trim(),
        plu: s.plu.trim(),
        grams: kind === 'WEIGHT' ? toGrams(s.value) : null,
        priceCents: kind === 'PRICE' ? pesosToCents(s.value) : null,
      }));
    if (body.length === 0) return setError('Escaneá al menos una etiqueta.');
    if (body.some((s) => !s.plu || !(s.grams || s.priceCents))) return setError('Completá en cada etiqueta el PLU y lo que dice impreso.');
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ candidates: ScaleConfig[] }>('/pos/scale/detect', { samples: body });
      setFound(res.candidates);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={
          <>
            <Sparkles size={16} style={{ verticalAlign: -2 }} /> Detectar automáticamente
          </>
        }
        subtitle="Imprimí una etiqueta, escaneala y copiá lo que dice impreso. Con 2 o 3 etiquetas distintas el resultado es más seguro."
      />
      <CardBody>
        <div className="bf-form">
          <div className="bf-segmented bf-segmented--text" role="group" aria-label="La etiqueta muestra">
            <button type="button" aria-pressed={kind === 'WEIGHT'} onClick={() => setKind('WEIGHT')}>
              La balanza manda el peso
            </button>
            <button type="button" aria-pressed={kind === 'PRICE'} onClick={() => setKind('PRICE')}>
              Manda el importe
            </button>
          </div>
          {samples.map((s, i) => (
            <div key={i} className="bf-detect-row">
              <Input
                value={s.code}
                onChange={(e) => setSample(i, 'code', e.target.value.replace(/\D/g, ''))}
                placeholder={`Etiqueta ${i + 1}: escaneá acá`}
                inputMode="numeric"
                aria-label={`Código de la etiqueta ${i + 1}`}
              />
              <Input
                value={s.plu}
                onChange={(e) => setSample(i, 'plu', e.target.value.replace(/\D/g, ''))}
                placeholder="PLU"
                inputMode="numeric"
                aria-label="PLU"
              />
              <Input
                value={s.value}
                onChange={(e) => setSample(i, 'value', e.target.value)}
                placeholder={kind === 'WEIGHT' ? 'Peso (1,250)' : 'Importe ($)'}
                inputMode="decimal"
                aria-label={kind === 'WEIGHT' ? 'Peso impreso' : 'Importe impreso'}
              />
            </div>
          ))}
          {samples.length < 3 && (
            <Button variant="ghost" onClick={() => setSamples((s) => [...s, { code: '', plu: '', value: '' }])}>
              + Otra etiqueta
            </Button>
          )}
          {error && <Callout tone="danger">{error}</Callout>}
          <Button icon={<Wand2 />} loading={busy} onClick={() => void detect()}>
            Detectar formato
          </Button>
          {found && found.length === 0 && (
            <Callout tone="warning">
              No encontramos un formato que lea esas etiquetas. Revisá que el PLU y el peso estén bien copiados, o probá con el importe. Si sigue sin
              andar, escribinos y lo configuramos con vos.
            </Callout>
          )}
          {found && found.length > 0 && (
            <div className="bf-candidates">
              <p className="bf-help">
                <span>
                  {found.length === 1 ? 'Encontramos el formato:' : 'Estos formatos leen bien tus etiquetas. El primero es el más probable:'}
                </span>
              </p>
              {found.map((cfg, i) => (
                <div key={i} className="bf-candidate">
                  <div>
                    <strong>{SCALE_PRESETS.find((p) => p.key === cfg.preset)?.label ?? 'Formato personalizado'}</strong>
                    <small>
                      Prefijo {cfg.prefixes.slice(0, 3).join(', ')}
                      {cfg.prefixes.length > 3 ? '…' : ''} · PLU {cfg.pluLength} dígitos{cfg.innerCheckDigit ? ' + control' : ''} ·{' '}
                      {cfg.valueType === 'WEIGHT'
                        ? `peso en ${cfg.weightUnitGrams === 1 ? 'gramos' : cfg.weightUnitGrams === 10 ? 'decagramos' : 'cien gramos'}`
                        : 'importe'}
                    </small>
                  </div>
                  <Button
                    size="sm"
                    variant={i === 0 ? 'primary' : 'secondary'}
                    onClick={() => {
                      onPick(cfg);
                      setFound(null);
                    }}
                  >
                    Usar este
                  </Button>
                </div>
              ))}
              <Help>Después de elegir, probalo con otra etiqueta y tocá “Guardar cambios”.</Help>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- Ticket */
function TicketTab({ value, onChange }: { value: PosSettings; onChange: (t: PosSettings['ticket']) => void }) {
  const { me, selectedBranchId } = useSession();
  const t = value.ticket;
  const set = <K extends keyof PosSettings['ticket']>(k: K, v: PosSettings['ticket'][K]) => onChange({ ...t, [k]: v });
  const branch = me.branches.find((b) => b.id === selectedBranchId) ?? me.branches[0];
  const sample = sampleSale(me.tenant.name, t, t.showBranchAddress ? (branch?.address ?? 'Av. Siempre Viva 742') : null);

  return (
    <div className="bf-settings-grid">
      <Card>
        <CardHeader title="Ticket" subtitle="Lo que sale impreso al cobrar. Es un comprobante interno, no una factura." />
        <CardBody>
          <div className="bf-form">
            <Field
              label="Ancho del papel"
              hint="Mirá el rollo: los de 80 mm son los anchos, los de 58 mm los angostos (impresoras chicas y portátiles)."
            >
              <div className="bf-segmented bf-segmented--text" role="group" aria-label="Ancho del papel">
                {([80, 58] as const).map((w) => (
                  <button key={w} type="button" aria-pressed={t.paperWidth === w} onClick={() => set('paperWidth', w)}>
                    {w} mm
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Nombre en el ticket" hint={`Vacío = ${me.tenant.name}`}>
              <Input value={t.businessName} onChange={(e) => set('businessName', e.target.value)} placeholder={me.tenant.name} maxLength={60} />
            </Field>
            <Field label="Texto arriba" hint="CUIT, teléfono, WhatsApp, Instagram… una cosa por renglón.">
              <Textarea
                rows={3}
                value={t.headerText}
                onChange={(e) => set('headerText', e.target.value)}
                placeholder={'WhatsApp 11 5555-5555\n@micarniceria'}
              />
            </Field>
            <Field label="Texto al pie">
              <Textarea rows={2} value={t.footerText} onChange={(e) => set('footerText', e.target.value)} />
            </Field>
            <SwitchRow
              title="Dirección de la sucursal"
              help="Se toma de Sucursales."
              checked={t.showBranchAddress}
              onChange={(v) => set('showBranchAddress', v)}
            />
            <SwitchRow
              title="Nombre de quien atendió"
              help="Útil cuando hay varios cajeros."
              checked={t.showCashier}
              onChange={(v) => set('showCashier', v)}
            />
            <SwitchRow title="Letra grande" help="Más fácil de leer; usa más papel." checked={t.largeText} onChange={(v) => set('largeText', v)} />
            <SwitchRow
              title="Imprimir al cobrar"
              help="Si lo apagás, el ticket se imprime solo cuando tocás “Imprimir”."
              checked={t.autoPrint}
              onChange={(v) => set('autoPrint', v)}
            />
            <SwitchRow
              title="Dos copias"
              help="Una para el cliente y otra para el negocio."
              checked={t.copies === 2}
              onChange={(v) => set('copies', v ? 2 : 1)}
            />
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader
          title="Vista previa"
          subtitle="Así sale con los datos de ejemplo."
          actions={
            <Button size="sm" icon={<Printer />} onClick={() => printSale(sample, t)}>
              Imprimir prueba
            </Button>
          }
        />
        <CardBody>
          <div className="bf-ticket-preview" data-width={t.paperWidth} data-large={t.largeText}>
            <div dangerouslySetInnerHTML={{ __html: ticketHtml(sample, t) }} />
          </div>
          <Help>
            ¿Sale cortado o muy chico? En el cuadro de impresión elegí <strong>Márgenes: ninguno</strong> y <strong>Escala: predeterminada</strong>.
            Si el papel es de 58 mm, cambiá el ancho acá.
          </Help>
        </CardBody>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------- Ventas */
function SalesTab({ value: s, onChange }: { value: PosSettings['sales']; onChange: (s: PosSettings['sales']) => void }) {
  const toggle = (m: PaymentMethod) => {
    const next = s.enabledPayments.includes(m) ? s.enabledPayments.filter((x) => x !== m) : [...s.enabledPayments, m];
    if (next.length > 0)
      onChange({
        ...s,
        enabledPayments: PAYMENT_METHODS.filter((x) => next.includes(x)),
      });
  };
  return (
    <div className="bf-form" style={{ gap: 16 }}>
      <PaymentsGuide />
      <MercadoPagoCard />
      <FranchiseStatusCard />
      <TerminalsCard value={s.terminals} onChange={(terminals) => onChange({ ...s, terminals })} />
      <Card>
        <CardHeader title="Ventas y pagos" subtitle="Reglas del mostrador para todas las sucursales." />
        <CardBody>
          <div className="bf-form" style={{ maxWidth: 640 }}>
            <Field
              label="Formas de pago que aceptás"
              hint="Solo aparecen en el cobro las que estén marcadas. Cuenta corriente = fiado: la venta queda anotada como deuda del cliente."
            >
              <div className="bf-chips">
                {PAYMENT_METHODS.filter((m) => !MP_METHODS.includes(m)).map((m) => (
                  <button key={m} type="button" className="bf-chip" aria-pressed={s.enabledPayments.includes(m)} onClick={() => toggle(m)}>
                    {PAYMENT_METHOD_LABELS[m]}
                  </button>
                ))}
              </div>
            </Field>
            <SwitchRow
              title="Vender aunque no haya stock cargado"
              help="Recomendado al empezar. El stock queda en negativo y aparece una alerta para corregirlo con la próxima compra."
              checked={s.allowSellWithoutStock}
              onChange={(v) => onChange({ ...s, allowSellWithoutStock: v })}
            />
            <Field
              label="Avisar deudas atrasadas después de (días)"
              hint="Si un cliente debe y no compra ni paga en esos días, aparece una alerta y se marca en Clientes."
            >
              <Input
                inputMode="numeric"
                value={s.accountOverdueDays}
                onChange={(e) =>
                  onChange({
                    ...s,
                    accountOverdueDays: Math.min(365, Math.max(1, Number(e.target.value.replace(/\D/g, '')) || 1)),
                  })
                }
                style={{ maxWidth: 120 }}
              />
            </Field>
            <Field
              label="Descuento máximo (%)"
              hint="Para encargados y cajeros con permiso de descuento. El dueño no tiene tope. 0 = no pueden hacer descuentos."
            >
              <Input
                inputMode="numeric"
                value={s.maxDiscountPercent}
                onChange={(e) =>
                  onChange({
                    ...s,
                    maxDiscountPercent: Math.min(100, Number(e.target.value.replace(/\D/g, '')) || 0),
                  })
                }
                style={{ maxWidth: 120 }}
              />
            </Field>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/** Qué hace cada medio de pago y cómo se cobra en el mostrador. */
function PaymentsGuide() {
  const rows: { name: string; how: string; auto: boolean }[] = [
    {
      name: 'Efectivo',
      how: 'Se elige “Efectivo”, se escribe con cuánto paga y BifeAI calcula el vuelto. Suma al efectivo esperado de la caja.',
      auto: true,
    },
    {
      name: 'Débito y Crédito (posnet del banco)',
      how: 'Se elige Débito o Crédito (con sus cuotas), se cobra en el posnet el mismo importe y se confirma cuando dice “Aprobado”. Se puede anotar el n.º de cupón. Al cerrar la caja se compara con el cierre de lote del posnet.',
      auto: false,
    },
    {
      name: 'Cuenta DNI',
      how: 'El cliente paga con su app escaneando el QR de la carnicería o con su posnet. Cuando la app Cuenta DNI Comercios (o el posnet) muestra “aprobado”, se elige “Cuenta DNI”.',
      auto: false,
    },
    {
      name: 'Transferencia',
      how: 'El cliente transfiere al alias o CBU de la carnicería. Se confirma al ver la plata acreditada (no alcanza con una captura). Si la cuenta es de Mercado Pago, el botón “Verificar en Mercado Pago” lo comprueba solo.',
      auto: false,
    },
    {
      name: 'QR / billetera',
      how: 'Otras billeteras con QR propio (Modo, Naranja X, etc.). Se elige cuando la billetera muestra el pago aprobado.',
      auto: false,
    },
    {
      name: 'Cuenta corriente (fiado)',
      how: 'La venta queda anotada como deuda del cliente. Respeta el límite de fiado de cada cliente.',
      auto: true,
    },
    {
      name: 'Mercado Pago QR y Point',
      how: 'Con la cuenta de Mercado Pago conectada (abajo): el QR sale con el importe exacto (en pantalla o el impreso de la caja) y el posnet Point recibe el importe solo. La venta se confirma sola cuando Mercado Pago aprueba el pago. Al anular, la plata se devuelve sola.',
      auto: true,
    },
  ];
  return (
    <Card>
      <CardHeader
        title="Cómo se cobra con cada medio"
        subtitle="Todos quedan registrados por separado: en la caja, en el cierre y en el Dashboard ves cuánto entró por cada uno."
      />
      <CardBody flush>
        <table className="bf-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td className="bf-cell-main" style={{ width: 230 }}>
                  {r.name}
                </td>
                <td className="bf-cell-sub" style={{ whiteSpace: 'normal' }}>
                  {r.how}
                </td>
                <td style={{ width: 120 }}>{r.auto ? <Badge tone="success">Automático</Badge> : <Badge>Lo confirma el cajero</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

/** Posnets de la carnicería: con más de uno, al cobrar con tarjeta se elige cuál se usó. */
function TerminalsCard({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [name, setName] = useState('');
  const add = () => {
    const n = name.trim();
    if (!n || value.includes(n) || value.length >= 10) return;
    onChange([...value, n]);
    setName('');
  };
  return (
    <Card>
      <CardHeader
        title="Posnets"
        subtitle="Si tenés más de un posnet (por ejemplo, uno de cada banco), cargalos: al cobrar con tarjeta se elige cuál se usó y al cerrar la caja ves el total de cada uno para compararlo con su cierre de lote."
      />
      <CardBody>
        <div className="bf-form" style={{ maxWidth: 640 }}>
          {value.length > 0 && (
            <div className="bf-chips">
              {value.map((t) => (
                <button key={t} type="button" className="bf-chip" aria-pressed onClick={() => onChange(value.filter((x) => x !== t))} title="Quitar">
                  {t} ✕
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="Ej.: Posnet Banco Nación"
              maxLength={40}
            />
            <Button onClick={add} disabled={!name.trim()}>
              Agregar
            </Button>
          </div>
          <p className="bf-hint" style={{ margin: 0 }}>
            Con un solo posnet no hace falta cargar nada. El <strong>cierre de lote</strong> es el resumen que imprime el posnet al final del día con
            todas las tarjetas cobradas: es lo que el banco te paga.
          </p>
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- Vencimientos */
function ExpiryTab({ value: s, onChange }: { value: PosSettings['stock']; onChange: (s: PosSettings['stock']) => void }) {
  const num = (v: string) => Math.min(3650, Number(v.replace(/\D/g, '')) || 0);
  return (
    <Card>
      <CardHeader
        title="Vencimientos"
        subtitle="Cada carnicería trabaja distinto: poné cuántos días dura cada tipo de producto. Se usa para la fecha de vencimiento de lo que producís o desposteás, y para avisarte antes de que venza."
      />
      <CardBody>
        <div className="bf-form" style={{ maxWidth: 640 }}>
          {SHELF_LIFE_GROUPS.map((g) => (
            <div key={g} className="bf-switch-row">
              <div>
                <strong>{SHELF_LIFE_GROUP_LABELS[g]}</strong>
                <small>Días de vida desde que entra (0 = sin vencimiento automático).</small>
              </div>
              <Input
                inputMode="numeric"
                value={s.shelfLifeDays[g]}
                onChange={(e) => onChange({ ...s, shelfLifeDays: { ...s.shelfLifeDays, [g]: num(e.target.value) } })}
                style={{ maxWidth: 90, textAlign: 'right' }}
                aria-label={`Días de ${SHELF_LIFE_GROUP_LABELS[g]}`}
              />
            </div>
          ))}
          <Help>Un producto puede tener sus propios días (Productos → editar). Lo envasado usa la fecha de la caja, que se carga al comprar.</Help>
          <Field label="Avisar antes de que venza (días)" hint="Te llega una alerta con esta anticipación.">
            <Input
              inputMode="numeric"
              value={s.expiryWarningDays}
              onChange={(e) => onChange({ ...s, expiryWarningDays: Math.min(60, num(e.target.value)) })}
              style={{ maxWidth: 120 }}
            />
          </Field>
          <Field
            label="Avisar faltantes de más del (%)"
            hint="Al hacer un recuento, te llega una alerta si de un producto falta más que este % de lo que se vendió desde el recuento anterior."
          >
            <Input
              inputMode="numeric"
              value={s.shortageAlertPercent}
              onChange={(e) => onChange({ ...s, shortageAlertPercent: Math.min(50, num(e.target.value)) })}
              style={{ maxWidth: 120 }}
            />
          </Field>
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- Equipo */
function TeamTab({ value: t, onChange }: { value: PosSettings['team']; onChange: (t: PosSettings['team']) => void }) {
  return (
    <Card>
      <CardHeader title="Equipo" subtitle="Gente que trabaja con la carnicería sin ser empleado, y la ganancia que buscás en los cortes." />
      <CardBody>
        <div className="bf-form" style={{ maxWidth: 640 }}>
          <SwitchRow
            title="Trabajo con un despostero"
            help="Viene a despostar (a veces a varias carnicerías) y cobra por media res o por kilo. Aparece “Despostero” en el menú para anotarle trabajos y pagarle."
            checked={t.externalButcher}
            onChange={(v) => onChange({ ...t, externalButcher: v })}
          />
          <Field
            label="Ganancia que buscás en los cortes (%)"
            hint="Después de despostar, te sugiere el precio de cada corte para llegar a esta ganancia."
          >
            <Input
              inputMode="numeric"
              value={t.targetMarginPercent}
              onChange={(e) => onChange({ ...t, targetMarginPercent: Math.min(90, Number(e.target.value.replace(/\D/g, '')) || 0) })}
              style={{ maxWidth: 120 }}
            />
          </Field>
        </div>
      </CardBody>
    </Card>
  );
}
